import { RootPage, RootPageTransparentPaper } from "@app/components/RootPage";
import { RootPageHeader } from "@app/components/RootPageHeader";
import { usePortfolioGroupInfo } from "../usePortfolioGroupInfo";
import { EditPortfolioGroupAssetClassSelection } from "./EditPortfolioGroupAssetClassSelection";
import { EditPortfolioGroupEditCompare } from "./EditPortfolioGroupEditCompare";
import { EditPortfolioGroupPublish } from "./EditPortfolioGroupPublish";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { useOptimizationJobsPuller } from "./useOptimizationJobsPuller";
import { editPortfolioGroupPages } from "@app/const/route.const";
import { EditPortfolioGroupCreateAllocations } from "./EditPortfolioGroupCreateAllocations";
import { EditPortfolioGroupSelection } from "./EditPortfolioGroupSelection";
import { EditPortfolioGroupAnalyze } from "./EditPortfolioGroupAnalyze";
import { EditPortfolioGroupOptimization } from "./EditPortfolioGroupOptimization";
import { SourceDataAsOf } from "../SourceDataAsOf";
import { CarModuleSectionCode } from "@app/hooks/useUserAccess";
import { AccessRouteControl } from "@app/components/AccessRouteControl";
import { CarNavigateButton } from "@app/components/NavigateButton";
import { ReactElement } from "react";
import { Box } from "@mui/material";

export const EditPortfolioGroupPage = () => {
  const { portfolioGroupId } = usePortfolioGroupParams();
  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });
  const { portfolioGroupVersionId } = groupInfo;

  useOptimizationJobsPuller(portfolioGroupVersionId);

  const withNextButton = (page: ReactElement) => (nextPageUrl: string) => (
    <>
      {page}
      <Box
        sx={{
          mt: "auto",
          display: "flex",
          gap: 2.5,
          justifyContent: "center",
        }}
      >
        <CarNavigateButton
          sx={{ mt: 5, fontSize: "18px" }}
          url={`..\\${nextPageUrl}`}
        >
          Next
        </CarNavigateButton>
      </Box>
    </>
  );

  return (
    <RootPage sx={{ backgroundColor: "white", paddingBottom: 0 }}>
      <RootPageHeader
        isSmall
        title={`Asset Allocation - ${groupInfo.title}`}
        isLoading={groupInfo.isLoading}
        items={[
          {
            label: "Asset Class Selection",
            url: editPortfolioGroupPages.assetClassSelection,
            moduleSection:
              CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_ASSET_CLASS_SELECTION,
          },
          {
            label: "Create Allocations",
            hidden: groupInfo.isPublished,
            url: editPortfolioGroupPages.createAllocations,
            moduleSection:
              CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_CREATE_ALLOCATIONS,
          },
          {
            label: "Optimization",
            url: editPortfolioGroupPages.optimization,
            moduleSection:
              CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_OPTIMIZATION,
            hidden: !groupInfo.isPublished,
          },
          {
            label: "Edit/Compare",
            url: editPortfolioGroupPages.editCompare,
            moduleSection:
              CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_EDIT_COMPARE,
            hidden: !groupInfo.isPublished,
          },
          {
            label: "Analyze",
            url: editPortfolioGroupPages.analyze,
            moduleSection: CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_ANALYZE,
            hidden: !groupInfo.isPublished,
          },
          {
            label: "Selection",
            url: editPortfolioGroupPages.selection,
            moduleSection:
              CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_SELECTION,
            hidden: !groupInfo.isPublished,
          },
          // {
          //   label: "Reports",
          //   url: editPortfolioGroupPages.reportsUrl,
          //   hidden: !groupInfo.isPublished,
          // },
          {
            label: "Publish",
            url: editPortfolioGroupPages.publish,
            moduleSection: CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_PUBLISH,
            hidden: !groupInfo.isPublished,
          },
        ]}
      />
      <RootPageTransparentPaper>
        <SourceDataAsOf />
        <AccessRouteControl
          items={[
            {
              path: editPortfolioGroupPages.assetClassSelection,
              element: withNextButton(
                <EditPortfolioGroupAssetClassSelection />,
              )(editPortfolioGroupPages.createAllocations),
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_ASSET_CLASS_SELECTION,
            },
            {
              path: editPortfolioGroupPages.createAllocations,
              hidden: groupInfo.isPublished,
              element: <EditPortfolioGroupCreateAllocations />,
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_CREATE_ALLOCATIONS,
            },
            {
              path: editPortfolioGroupPages.optimization,
              element: <EditPortfolioGroupOptimization />,
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_OPTIMIZATION,
            },
            {
              path: editPortfolioGroupPages.editCompare,
              element: withNextButton(<EditPortfolioGroupEditCompare />)(
                editPortfolioGroupPages.analyze,
              ),
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_EDIT_COMPARE,
            },
            {
              path: editPortfolioGroupPages.analyze,
              element: withNextButton(<EditPortfolioGroupAnalyze />)(
                editPortfolioGroupPages.selection,
              ),
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_ANALYZE,
            },
            {
              path: editPortfolioGroupPages.selection,
              element: withNextButton(<EditPortfolioGroupSelection />)(
                editPortfolioGroupPages.publish,
              ),
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_SELECTION,
            },
            // {
            //   path: editPortfolioGroupPages.reportsPath,
            //   element: <EditPortfolioGroupReports />,
            // },
            {
              path: editPortfolioGroupPages.publish,
              element: <EditPortfolioGroupPublish />,
              moduleSection:
                CarModuleSectionCode.ANALYZE_PORTFOLIO_GROUP_PUBLISH,
            },
          ]}
          defaultPath={editPortfolioGroupPages.assetClassSelection}
        />
      </RootPageTransparentPaper>
    </RootPage>
  );
};
